import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ClientManagement } from './pages/ClientManagement';
import { VendorSupplierManagement } from './pages/VendorSupplierManagement';
import { WorkOrders } from './pages/WorkOrders';
import { PurchaseOrders } from './pages/PurchaseOrders';
import { LineItemMaster } from './pages/LineItemMaster';
import { AppLayout } from './components/AppLayout';
import { Toaster } from './components/ui/sonner';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { ProjectHub } from './pages/ProjectHub';
import { ActionCenter } from './pages/ActionCenter';
import { UserManagement } from './pages/UserManagement';
import { BOQEditor } from './pages/BOQEditor';
import { CompanyManagement } from './pages/CompanyManagement';
import { TradeManagement } from './pages/TradeManagement';
import { MaterialTracking } from './pages/MaterialTracking';
import { ManpowerPlanning } from './pages/ManpowerPlanning';
import { GanttChart } from './pages/GanttChart';
import { PurchaseSchedule } from './pages/PurchaseSchedule';
import { BudgetVsActual } from './pages/BudgetVsActual';
import ClientSnapshot from './pages/ClientSnapshot';
import ExecutionCenter from './pages/ExecutionCenter';
import WeeklySummary from './pages/WeeklySummary';
import PermissionManager from './pages/PermissionManager';
import AuditCenter from './pages/AuditCenter';
import MaterialMaster from './pages/MaterialMaster';
import ProcurementCenter from './pages/ProcurementCenter';
import ProcurementReconciliation from './pages/ProcurementReconciliation';
import CreditDebitNoteRegister from './pages/CreditDebitNoteRegister';
import RFQGenerator from './pages/RFQGenerator';
import RunningBillGenerator from './pages/RunningBillGenerator';
import GanttSimulation from './pages/GanttSimulation';
import { ReportsCenter } from './pages/ReportsCenter';
import { VariationOrders } from './pages/VariationOrders';
import { FounderDashboard } from './pages/FounderDashboard';
import { SmokeTest } from './pages/SmokeTest';
import { ResourceSummary } from './pages/ResourceSummary';
import MasterStudio from './pages/MasterStudio';
import { ItemSuggestions } from './pages/ItemSuggestions';

function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
          <Route path="/companies" element={<ProtectedRoute><AppLayout><CompanyManagement /></AppLayout></ProtectedRoute>} />
          <Route path="/trades" element={<ProtectedRoute><AppLayout><TradeManagement /></AppLayout></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><AppLayout><ClientManagement /></AppLayout></ProtectedRoute>} />
          <Route path="/vendors" element={<ProtectedRoute><AppLayout><VendorSupplierManagement /></AppLayout></ProtectedRoute>} />
          <Route path="/line-item-master" element={<ProtectedRoute><AppLayout><LineItemMaster /></AppLayout></ProtectedRoute>} />
          <Route path="/item-suggestions" element={<ProtectedRoute><AppLayout><ItemSuggestions /></AppLayout></ProtectedRoute>} />

          {/* Action Center */}
          <Route path="/action-center" element={<ProtectedRoute><AppLayout><ActionCenter /></AppLayout></ProtectedRoute>} />
          {/* User Management */}
          <Route path="/user-management" element={<ProtectedRoute><AppLayout><UserManagement /></AppLayout></ProtectedRoute>} />
          {/* BOQ Module */}
          <Route path="/projects/:projectId" element={<ProtectedRoute><AppLayout><ProjectHub /></AppLayout></ProtectedRoute>} />
          <Route path="/projects/:projectId/quotes/:quoteId" element={<ProtectedRoute><AppLayout><BOQEditor /></AppLayout></ProtectedRoute>} />

          {/* Project Control Tower */}
          <Route path="/projects/:projectId/control/material" element={<ProtectedRoute><AppLayout><MaterialTracking /></AppLayout></ProtectedRoute>} />
          <Route path="/projects/:projectId/control/manpower" element={<ProtectedRoute><AppLayout><ManpowerPlanning /></AppLayout></ProtectedRoute>} />
          <Route path="/projects/:projectId/control/gantt" element={<ProtectedRoute><AppLayout><GanttChart /></AppLayout></ProtectedRoute>} />
          <Route path="/projects/:projectId/control/purchase" element={<ProtectedRoute><AppLayout><PurchaseSchedule /></AppLayout></ProtectedRoute>} />
          <Route path="/projects/:projectId/control/budget" element={<ProtectedRoute><AppLayout><BudgetVsActual /></AppLayout></ProtectedRoute>} />
          <Route path="/projects/:projectId/control/work-orders" element={<ProtectedRoute><AppLayout><WorkOrders /></AppLayout></ProtectedRoute>} />
          <Route path="/projects/:projectId/control/purchase-orders" element={<ProtectedRoute><AppLayout><PurchaseOrders /></AppLayout></ProtectedRoute>} />

          {/* Execution Center */}
          <Route path="/projects/:projectId/execution" element={<ProtectedRoute><AppLayout><ExecutionCenter /></AppLayout></ProtectedRoute>} />
          <Route path="/projects/:projectId/execution/weekly-summary" element={<ProtectedRoute><AppLayout><WeeklySummary /></AppLayout></ProtectedRoute>} />

          {/* Variation Orders */}
          <Route path="/projects/:projectId/variation-orders" element={<ProtectedRoute><AppLayout><VariationOrders /></AppLayout></ProtectedRoute>} />

          {/* Resource Summary */}
          <Route path="/projects/:projectId/resources" element={<ProtectedRoute><AppLayout><ResourceSummary /></AppLayout></ProtectedRoute>} />

          {/* Gantt Simulation */}
          <Route path="/projects/:projectId/gantt-simulation" element={<ProtectedRoute><AppLayout><GanttSimulation /></AppLayout></ProtectedRoute>} />

          {/* Reports Center */}
          <Route path="/reports" element={<ProtectedRoute><AppLayout><ReportsCenter /></AppLayout></ProtectedRoute>} />

          {/* Founder Dashboard — Admin/Super Admin only */}
          <Route path="/command-center" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AppLayout><FounderDashboard /></AppLayout></ProtectedRoute>} />

          {/* System Health Check — Admin/Super Admin only */}
          <Route path="/system-health" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AppLayout><SmokeTest /></AppLayout></ProtectedRoute>} />

          {/* RBAC & Audit — Admin/Super Admin only */}
          <Route path="/permissions" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AppLayout><PermissionManager /></AppLayout></ProtectedRoute>} />
          <Route path="/audit" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AppLayout><AuditCenter /></AppLayout></ProtectedRoute>} />
          <Route path="/material-master" element={<ProtectedRoute><AppLayout><MaterialMaster /></AppLayout></ProtectedRoute>} />
          <Route path="/procurement" element={<ProtectedRoute><AppLayout><ProcurementCenter /></AppLayout></ProtectedRoute>} />
          <Route path="/procurement/reconciliation" element={<ProtectedRoute><AppLayout><ProcurementReconciliation /></AppLayout></ProtectedRoute>} />
          <Route path="/adjustments" element={<ProtectedRoute><AppLayout><CreditDebitNoteRegister /></AppLayout></ProtectedRoute>} />
          <Route path="/rfq-generator" element={<ProtectedRoute><AppLayout><RFQGenerator /></AppLayout></ProtectedRoute>} />
          <Route path="/running-bill" element={<ProtectedRoute><AppLayout><RunningBillGenerator /></AppLayout></ProtectedRoute>} />

          {/* Public Client Snapshot (no auth) */}
          <Route path="/snapshot/:token" element={<ClientSnapshot />} />

          {/* Legacy material tracking route */}
          <Route path="/quotes/:quoteId/materials" element={<ProtectedRoute><AppLayout><MaterialTracking /></AppLayout></ProtectedRoute>} />

          {/* Master Studio */}
          <Route path="/master-studio" element={<ProtectedRoute><AppLayout><MasterStudio /></AppLayout></ProtectedRoute>} />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
